import type { Project, Task, WorkflowColumn } from '@portfolio-pm/shared';
import { ChevronRight, Plus, X } from 'lucide-react';
import type { ReactNode, RefObject } from 'react';
import { getDocStore } from '../../../store/doc-store.ts';
import { ScrollViewport } from '../../layout/ScrollViewport.tsx';
import type { TaskWorkspaceView } from '../task-workspace-model.ts';
import { commitOnEnter, getRepeatLabel, parseTags } from './detail-utils.ts';
import { CalendarDateInput, SmartEstimateField } from './fields.tsx';

/**
 * Mobile read-first edit sheet for grouped task fields (REQ-UI-004).
 * Extracted from TaskDetailPanel.tsx.
 */
export function TaskDetailEditPanel({
  projects,
  workflowColumns,
  selectedTask,
  titleFieldRef,
  currentView,
  onChangeView,
  onClose,
}: {
  projects: Project[];
  workflowColumns: WorkflowColumn[];
  selectedTask: Task;
  titleFieldRef: RefObject<HTMLInputElement | HTMLTextAreaElement>;
  currentView: TaskWorkspaceView;
  onChangeView(view: TaskWorkspaceView): void;
  onClose(): void;
}): JSX.Element {
  const store = getDocStore();

  const saveAndClose = (): void => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    onClose();
  };

  return (
    <aside
      data-testid="task-detail-panel"
      className="flex h-full min-h-0 flex-col overflow-hidden bg-[color:var(--app-workspace)] text-[color:var(--app-text)]"
    >
      <div className="grid min-h-24 shrink-0 grid-cols-[4rem_minmax(0,1fr)_5.5rem] items-center gap-3 rounded-b-[2rem] bg-[color:var(--app-panel)] px-5 py-4">
        <button
          type="button"
          aria-label="Cancel editing"
          onClick={onClose}
          className="flex h-12 w-12 items-center justify-center rounded-full border border-[color:var(--app-border)] bg-[color:var(--app-control-bg)] text-[color:var(--app-text)] shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] transition active:scale-[0.98]"
        >
          <X aria-hidden="true" size={24} strokeWidth={2.2} />
        </button>
        <h2 className="truncate text-center text-xl font-semibold tracking-tight">Edit Task</h2>
        <button
          type="button"
          onClick={saveAndClose}
          className="h-12 rounded-full bg-[#139cf4] px-5 text-base font-semibold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.22)] transition active:scale-[0.98]"
        >
          Save
        </button>
      </div>

      <ScrollViewport className="min-h-0 flex-1" viewportClassName="task-detail-viewport h-full">
        <div className="grid gap-6 px-5 pb-12 pt-6">
          <EditCard>
            <input
              id={`${selectedTask.id}-title-field`}
              key={`${selectedTask.id}-title-edit`}
              ref={titleFieldRef as RefObject<HTMLInputElement>}
              aria-label="Title"
              defaultValue={selectedTask.title}
              onBlur={(event) => store.renameTask(selectedTask.id, event.target.value)}
              onKeyDown={commitOnEnter}
              className="w-full border-0 bg-transparent py-1 text-lg text-[color:var(--app-text)] outline-none"
            />
            <EditRow label="Priority">
              <select
                id={`${selectedTask.id}-priority-field`}
                aria-label="Priority"
                value={selectedTask.priority}
                onChange={(event) =>
                  store.updateTask(selectedTask.id, {
                    priority: Number(event.target.value) as Task['priority'],
                  })
                }
                className="max-w-40 bg-transparent text-right text-[color:var(--app-text-muted)] outline-none"
              >
                <option value={0}>None</option>
                <option value={1}>1 - High</option>
                <option value={2}>2 - Medium</option>
                <option value={3}>3 - Low</option>
              </select>
            </EditRow>
          </EditCard>

          <EditCard>
            <EditRow label="Start">
              <CalendarDateInput
                dateId={`${selectedTask.id}-scheduled-field`}
                key={`${selectedTask.id}-scheduled-edit`}
                value={selectedTask.scheduledAt}
                hasTime={selectedTask.scheduledAtHasTime}
                onCommit={(value, hasTime) =>
                  store.updateTask(selectedTask.id, {
                    scheduledAt: value,
                    scheduledAtHasTime: hasTime,
                  })
                }
              />
            </EditRow>
            <EditRow label="Due">
              <CalendarDateInput
                dateId={`${selectedTask.id}-due-field`}
                key={`${selectedTask.id}-due-edit`}
                value={selectedTask.dueAt}
                hasTime={selectedTask.dueAtHasTime}
                onCommit={(value, hasTime) =>
                  store.updateTask(selectedTask.id, {
                    dueAt: value,
                    dueAtHasTime: hasTime,
                  })
                }
              />
            </EditRow>
            <EditRow label="Repeats">
              <span className="truncate text-right text-[color:var(--app-text-muted)]">
                {getRepeatLabel(selectedTask)}
              </span>
            </EditRow>
            <EditRow label="Estimate">
              <SmartEstimateField
                id={`${selectedTask.id}-estimate-field`}
                key={`${selectedTask.id}-estimate-edit`}
                value={selectedTask.estimateMinutes}
                onChange={(minutes) =>
                  store.updateTask(selectedTask.id, { estimateMinutes: minutes })
                }
              />
            </EditRow>
          </EditCard>

          <EditCard>
            <EditRow label="List">
              <select
                id={`${selectedTask.id}-project-field`}
                aria-label="Project"
                value={selectedTask.projectId}
                onChange={(event) => {
                  const nextProjectId = event.target.value;
                  store.updateTask(selectedTask.id, { projectId: nextProjectId });
                  store.setActiveProject(nextProjectId);
                  if (currentView.kind === 'project' && currentView.projectId !== nextProjectId) {
                    onChangeView({ kind: 'project', projectId: nextProjectId });
                  }
                }}
                className="max-w-44 bg-transparent text-right text-[color:var(--app-text-muted)] outline-none"
              >
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </EditRow>
            <EditRow label="Workflow">
              <select
                id={`${selectedTask.id}-workflow-field`}
                aria-label="Workflow"
                value={selectedTask.workflowState}
                onChange={(event) =>
                  store.updateTask(selectedTask.id, {
                    workflowState: event.target.value as Task['workflowState'],
                  })
                }
                className="max-w-44 bg-transparent text-right text-[color:var(--app-text-muted)] outline-none"
              >
                {workflowColumns.map((column) => (
                  <option key={column.id} value={column.id}>
                    {column.name}
                  </option>
                ))}
              </select>
            </EditRow>
            <EditRow label="Tags">
              <input
                id={`${selectedTask.id}-tags-field`}
                key={`${selectedTask.id}-tags-edit`}
                aria-label="Tags"
                defaultValue={selectedTask.tags.join(', ')}
                onBlur={(event) =>
                  store.updateTask(selectedTask.id, {
                    tags: parseTags(event.target.value),
                  })
                }
                onKeyDown={commitOnEnter}
                className="min-w-0 flex-1 bg-transparent text-right text-[color:var(--app-text-muted)] outline-none"
              />
            </EditRow>
            <EditRow label="Location">
              <input
                id={`${selectedTask.id}-location-field`}
                key={`${selectedTask.id}-location-edit`}
                aria-label="Location"
                defaultValue={selectedTask.location ?? ''}
                onBlur={(event) =>
                  store.updateTask(selectedTask.id, {
                    location: event.target.value.trim() || undefined,
                  })
                }
                onKeyDown={commitOnEnter}
                className="min-w-0 flex-1 bg-transparent text-right text-[color:var(--app-text-muted)] outline-none"
              />
            </EditRow>
            <EditRow label="URL" muted>
              <input
                id={`${selectedTask.id}-url-field`}
                key={`${selectedTask.id}-url-edit`}
                aria-label="URL"
                defaultValue={selectedTask.url ?? ''}
                onBlur={(event) =>
                  store.updateTask(selectedTask.id, {
                    url: event.target.value.trim() || undefined,
                  })
                }
                onKeyDown={commitOnEnter}
                className="min-w-0 flex-1 bg-transparent text-right text-[color:var(--app-text-muted)] outline-none"
              />
            </EditRow>
          </EditCard>

          <button
            type="button"
            className="flex h-14 items-center gap-4 rounded-[1.75rem] bg-[color:var(--app-panel-muted)] px-6 text-left text-[color:var(--app-text-soft)]"
          >
            <Plus aria-hidden="true" size={20} />
            Add field
          </button>
        </div>
      </ScrollViewport>
    </aside>
  );
}

function EditCard({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="pane-divide-y rounded-[1.75rem] bg-[color:var(--app-panel-muted)] px-6 py-4">
      {children}
    </div>
  );
}

function EditRow({
  label,
  children,
  muted = false,
}: {
  label: string;
  children: ReactNode;
  muted?: boolean;
}): JSX.Element {
  return (
    <div className="grid min-h-14 grid-cols-[6rem_minmax(0,1fr)_1rem] items-center gap-3">
      <span
        className={`text-base ${muted ? 'text-[color:var(--app-text-soft)]' : 'text-[color:var(--app-text)]'}`}
      >
        {label}
      </span>
      <div className="flex min-w-0 justify-end">{children}</div>
      <ChevronRight aria-hidden="true" size={17} className="text-[color:var(--app-text-soft)]" />
    </div>
  );
}
